import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { GeolocationPositionTransferable, InstrumentationEventDomEventFired, InstrumentationEventGeolocationRequestSuccess } from "../../lib/Events/InstrumentationEvent";
import { executeEventually } from "../../lib/utils/executeEventually";
import { DbRecordedEvent, DbRecordedEventByCategoryAndName } from "../../State/EvidenceDB/DatabaseTypes";
import { DbEventsTableReader } from "../../State/EvidenceDB/DbEventsTableReader";
import { DbRecordingEntryReader } from "../../State/EvidenceDB/DbRecordingEntryReader";
import { NewEventAddedToDatabase } from "../../State/StateEvents";

const notableMsBetweenInputChangeEvents = 3000;

type DomEventFromWhichToGenerateSearchTerm = DbRecordedEvent<InstrumentationEventDomEventFired<"change" | "input" | "paste">> & { raw: { value: string} };
const isDomEventFromWhichToGenerateSearchTerm = (e: DbRecordedEvent<InstrumentationEventDomEventFired>): e is DomEventFromWhichToGenerateSearchTerm  => 
	(e.raw.domOrCustomEventName === "change" || e.raw.domOrCustomEventName === "input" || e.raw.domOrCustomEventName === "paste") &&
	e.raw.value != null && e.raw.value.length > 2;

export class InferrerOfSearchTermsFromDomEvents {
	highestDomEventFiredEventIndex: number | undefined;
	highestGeolocationWatchPositionSuccessEventIndex: number | undefined;
	highestGeolocationGetCurrentPositionSuccessEventIndex: number | undefined;
	readonly eventsTableReader: DbEventsTableReader;

	protected _domEventsFromWhichToGenerateSearchTerms = observable.array<DomEventFromWhichToGenerateSearchTerm>();
	protected _geolocationPositionSuccessEventsObserved = observable.array<InstrumentationEventGeolocationRequestSuccess>();
	get domEventsFromWhichToGenerateSearchTerms() { return this._domEventsFromWhichToGenerateSearchTerms; }
	get geolocationPositionSuccessEventsObserved() { return this._geolocationPositionSuccessEventsObserved; }

	updateDomEventFiredEvents = executeEventually( async () => {
		const newDomEventsFired = await this.eventsTableReader.getEventsForCategoryAndName({category: "Instrumentation", name: "DomEventFired", 
			...(this.highestDomEventFiredEventIndex == null ? {startingAtIndexInclusive: 0} : {startingAtIndexExclusive: this.highestDomEventFiredEventIndex})});
		for (const event of newDomEventsFired) {
			this.highestDomEventFiredEventIndex = Math.max(this.highestDomEventFiredEventIndex ?? 0, event.eventIndex);
			this.addDomEventFiredEvent(event);
		}
	});

	addGeolocationSuccessEvent = action( (position: InstrumentationEventGeolocationRequestSuccess) => {
		this._geolocationPositionSuccessEventsObserved.push(position);
	});

	updateGeolocationWatchPositionSuccessEvents = executeEventually( async () => {
		const newGeolocationEvent = await this.eventsTableReader.getEventsForCategoryAndName({category: "Instrumentation", name: `GeolocationRequest.watchPosition.success`,
			...(this.highestGeolocationWatchPositionSuccessEventIndex == null ? {startingAtIndexInclusive: 0} : {startingAtIndexExclusive: this.highestGeolocationWatchPositionSuccessEventIndex})});
		for (const event of newGeolocationEvent) {
			this.highestGeolocationWatchPositionSuccessEventIndex = Math.max(this.highestGeolocationWatchPositionSuccessEventIndex ?? 0, event.eventIndex);
			this.addGeolocationSuccessEvent(event);
		}
	});

	updateGeolocationGetCurrentPositionSuccessEvents = executeEventually( async () => {
		const newGeolocationEvent = await this.eventsTableReader.getEventsForCategoryAndName({category: "Instrumentation", name: `GeolocationRequest.getCurrentPosition.success`,
			...(this.highestGeolocationGetCurrentPositionSuccessEventIndex == null ? {startingAtIndexInclusive: 0} : {startingAtIndexExclusive: this.highestGeolocationGetCurrentPositionSuccessEventIndex})});
		for (const event of newGeolocationEvent) {
			this.highestGeolocationGetCurrentPositionSuccessEventIndex = Math.max(this.highestGeolocationGetCurrentPositionSuccessEventIndex ?? 0, event.eventIndex);
			this.addGeolocationSuccessEvent(event);
		}
	});

	constructor(
		protected readonly recordingReader: DbRecordingEntryReader
	) {
		this.eventsTableReader = DbEventsTableReader.forRecordingUuid(recordingReader.recordingUuid);
		NewEventAddedToDatabase.addListener( e => {
			switch (e.eventName) {
				case "DomEventFired":
					this.updateDomEventFiredEvents();
					break;
				case "GeolocationRequest.getCurrentPosition.success":
					this.updateGeolocationGetCurrentPositionSuccessEvents();
					break;
				case "GeolocationRequest.watchPosition.success":
					this.updateGeolocationWatchPositionSuccessEvents();
					break;
			}
		});
		this.updateDomEventFiredEvents();
		this.updateGeolocationGetCurrentPositionSuccessEvents();
		this.updateGeolocationWatchPositionSuccessEvents();
	}

	addDomEventFiredEvent = action ( (event: DbRecordedEvent<InstrumentationEventDomEventFired>) => {
		if (isDomEventFromWhichToGenerateSearchTerm(event)) {
			this._domEventsFromWhichToGenerateSearchTerms.push(event);
		}
	});


	@computed get eventsToUseAsSearchTerms() {
		const byElementUuid = new Map<string, DomEventFromWhichToGenerateSearchTerm[]>();
		for (const newEvent of this.domEventsFromWhichToGenerateSearchTerms) {
			const priorEventsToKeep = (byElementUuid.get(newEvent.raw.elementUUID) ?? []).filter(
				oldEvent => oldEvent.raw.whenObservedMs < newEvent.raw.whenObservedMs - notableMsBetweenInputChangeEvents
			);
			byElementUuid.set(newEvent.raw.elementUUID, [...priorEventsToKeep, newEvent]);
		}
		const bySearchTerm = new Map<string, DomEventFromWhichToGenerateSearchTerm | InstrumentationEventGeolocationRequestSuccess>();
		for (const events of byElementUuid.values()) {
			for (const event of events) {
				if (!bySearchTerm.has(event.raw.value)) {
					bySearchTerm.set(event.raw.value, event);
				}
			}
		}
		const searchTermsToIgnore = new Set<string>(
			this.recordingReader.recording.domGeneratedSearchTermsToIgnore ?? []
		)
		for (const event of this.geolocationPositionSuccessEventsObserved) {
			const {longitude, latitude} = event.raw.position.coords;
			for (const coord of [longitude, latitude]) {
				if (coord != null) {
					// keep 6 digits of precision (one character is "." and one may be '-')
					const coordString = coord.toString().slice(0, coord < 0 ? 8 : 7);					
					bySearchTerm.set(coordString, event);
				}
			}
		}
		return [...bySearchTerm.entries()]
			.filter( ([searchTerm]) => !searchTermsToIgnore.has(searchTerm));
	};

	@computed get searchTermsFromDomEventsChronological() {
		return this.eventsToUseAsSearchTerms.map( ([searchTerm]) => searchTerm);
	}

	@computed get searchTermsFromDomEventsSortedAlphanumeric() {
		return this.searchTermsFromDomEventsChronological.sort();
	}



}
